import type { ErrorInfo } from 'react';
import Cookies from 'js-cookie';

import { COOKIE } from '@/components/layout/utils';
import posthog from '@/lib/posthog/client';

import { getErrorObject } from './error';
import { redactError, redactString } from './redact';

export { getErrorObject } from './error';

const captured = new WeakSet<Error>();

export const canCaptureBrowserAnalytics = (): boolean =>
  typeof document !== 'undefined' &&
  Cookies.get(COOKIE.posthogIdentifyKey) === 'true' &&
  (process.env.NEXT_PUBLIC_NODE_ENV !== 'development' || process.env.NEXT_PUBLIC_POSTHOG_DEV_TRACKING === 'true');

function captureAnalyticsError(error: Error, source: string): void {
  if (!canCaptureBrowserAnalytics() || captured.has(error)) return;
  posthog.captureException(redactError(error), { source: redactString(source, 300) });
  captured.add(error);
}

export interface ErrorReportingOptions {
  source: string;
  component?: string;
  errorInfo?: ErrorInfo;
  queryKey?: unknown;
  additionalContext?: Record<string, unknown>;
}

export interface FailedEventOptions {
  eventName: string;
  source: string;
  additionalContext?: Record<string, unknown>;
}

/**
 * Centralized error reporting function for PostHog
 *
 * @param error - The error object
 * @param options - Additional context for error reporting
 */
export const captureError = (error: Error, options: ErrorReportingOptions): void => {
  try {
    captureAnalyticsError(error, options.source);
  } catch {
    // Reporting is best effort and must not interrupt the user's operation.
  }
};

/**
 * Wrapper function for capturing failed events in PostHog
 * Simplifies the process of tracking failed operations
 *
 * @param error - Any value that will be converted to an Error object
 * @param options - Event options including event name and source
 */
export const captureFailedEvent = (error: unknown, options: FailedEventOptions): void => {
  try {
    const errorObject = getErrorObject(error);

    captureAnalyticsError(errorObject, options.source);
  } catch {
    // Reporting is best effort and must not interrupt the user's operation.
  }
};
